import React, { useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    MenuItem, Box, Typography, IconButton, InputAdornment, CircularProgress, Divider,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import { useSnackbar } from "notistack";
import v2Api from "../../utils/v2Api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const emptyRecipient = () => ({ email: "", name: "", phone: "", notes: "" });
const todayStr = () => new Date().toISOString().slice(0, 10);

const GenerateLinkDialog = ({ open, onClose, onGenerated }) => {
    const { enqueueSnackbar } = useSnackbar();

    const [tests, setTests] = useState([]);
    const [testId, setTestId] = useState("");
    const [instructions, setInstructions] = useState("");
    const [recipients, setRecipients] = useState([emptyRecipient()]);
    const [expiresOn, setExpiresOn] = useState(""); // YYYY-MM-DD, optional
    const [submitting, setSubmitting] = useState(false);
    const [results, setResults] = useState(null); // [{ email, url }] after generation

    useEffect(() => {
        if (!open) return;
        setTestId("");
        setInstructions("");
        setRecipients([emptyRecipient()]);
        setExpiresOn("");
        setResults(null);
        (async () => {
            try {
                const res = await v2Api.get("/placement/tests");
                setTests(res.data?.data || []);
            } catch {
                enqueueSnackbar("Placement 테스트 목록을 불러오지 못했습니다.", { variant: "error" });
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const onTestChange = async (e) => {
        const id = e.target.value;
        setTestId(id);
        try {
            const res = await v2Api.get(`/placement/tests/${id}/instructions`);
            setInstructions(res.data?.data?.instructions || "");
        } catch { /* leave as-is */ }
    };

    const setRecipient = (idx, field, val) =>
        setRecipients((rs) => rs.map((r, i) => (i === idx ? { ...r, [field]: val } : r)));
    const addRecipient = () => setRecipients((rs) => [...rs, emptyRecipient()]);
    const removeRecipient = (idx) => setRecipients((rs) => rs.filter((_, i) => i !== idx));

    const handleGenerate = async () => {
        if (!testId) { enqueueSnackbar("테스트를 선택하세요.", { variant: "warning" }); return; }
        const cleaned = recipients
            .map((r) => ({ ...r, email: r.email.trim() }))
            .filter((r) => r.email);
        if (cleaned.length === 0) { enqueueSnackbar("최소 한 명의 이메일을 입력하세요.", { variant: "warning" }); return; }
        const bad = cleaned.find((r) => !EMAIL_RE.test(r.email));
        if (bad) { enqueueSnackbar(`잘못된 이메일: ${bad.email}`, { variant: "warning" }); return; }

        try {
            setSubmitting(true);
            const res = await v2Api.post("/placement/links", {
                test_id: testId,
                instructions: instructions || null,
                expires_on: expiresOn || null,
                recipients: cleaned.map((r) => ({
                    email: r.email, name: r.name || null, phone: r.phone || null, notes: r.notes || null,
                })),
            });
            setResults(res.data?.data || []);
            enqueueSnackbar(`${res.data?.data?.length || 0}개의 링크가 생성되었습니다.`, { variant: "success" });
            onGenerated?.();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "링크 생성에 실패했습니다.", { variant: "error" });
        } finally {
            setSubmitting(false);
        }
    };

    const copyOne = (url) => {
        navigator.clipboard.writeText(url);
        enqueueSnackbar("클립보드에 복사되었습니다.", { variant: "success" });
    };
    const copyAll = () => {
        navigator.clipboard.writeText(results.map((r) => `${r.email}: ${r.url}`).join("\n"));
        enqueueSnackbar("모든 링크를 복사했습니다.", { variant: "success" });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Generate placement-test links</DialogTitle>
            <DialogContent dividers>
                {results ? (
                    <Box>
                        <Typography variant="subtitle2" color="success.main" gutterBottom>
                            ✓ {results.length} link{results.length !== 1 ? "s" : ""} generated — each single-use
                        </Typography>
                        {results.map((r) => (
                            <Box key={r.url} display="flex" gap={1} alignItems="center" mb={1}>
                                <Typography sx={{ width: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {r.email}
                                </Typography>
                                <TextField
                                    fullWidth size="small" value={r.url}
                                    InputProps={{
                                        readOnly: true,
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => copyOne(r.url)}><ContentCopyIcon fontSize="small" /></IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Box display="flex" flexDirection="column" gap={2} pt={1}>
                        <TextField select required label="Test" value={testId} onChange={onTestChange}>
                            {tests.length === 0 && <MenuItem value="" disabled>No Placement-Test-typed tests found</MenuItem>}
                            {tests.map((t) => <MenuItem key={t.id} value={t.id}>{t.title}</MenuItem>)}
                        </TextField>

                        {/* Recipients (bulk) */}
                        <Box>
                            <Typography variant="caption" color="text.secondary">Recipients</Typography>
                            {recipients.map((r, idx) => {
                                const invalid = r.email.trim() !== "" && !EMAIL_RE.test(r.email.trim());
                                return (
                                    <Box key={idx} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5, mt: 1 }}>
                                        <Box display="flex" gap={1} alignItems="flex-start">
                                            <TextField
                                                size="small" label="Email" required value={r.email}
                                                onChange={(e) => setRecipient(idx, "email", e.target.value)}
                                                error={invalid} helperText={invalid ? "Invalid email" : " "}
                                                sx={{ flex: 2 }}
                                            />
                                            <TextField size="small" label="Name" value={r.name}
                                                onChange={(e) => setRecipient(idx, "name", e.target.value)} sx={{ flex: 1 }} helperText=" " />
                                            <TextField size="small" label="Phone" value={r.phone}
                                                onChange={(e) => setRecipient(idx, "phone", e.target.value)} sx={{ flex: 1 }} helperText=" " />
                                            <IconButton
                                                onClick={() => removeRecipient(idx)} disabled={recipients.length === 1}
                                                sx={{ mt: 0.5 }}
                                            >
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </Box>
                                        <TextField
                                            label="Notes (office-only, never shown to taker)" value={r.notes}
                                            onChange={(e) => setRecipient(idx, "notes", e.target.value)}
                                            multiline minRows={2} fullWidth size="small"
                                        />
                                    </Box>
                                );
                            })}
                            <Button startIcon={<PersonAddAltIcon />} onClick={addRecipient} size="small" sx={{ mt: 0.5 }}>
                                Add recipient
                            </Button>
                        </Box>

                        <Divider />

                        <TextField
                            type="date" label="Valid through (end of day)" value={expiresOn}
                            onChange={(e) => setExpiresOn(e.target.value)}
                            InputLabelProps={{ shrink: true }} inputProps={{ min: todayStr() }}
                            sx={{ width: 260 }}
                            helperText="Optional — link expires at the end of this date. Blank = no expiry."
                        />

                        <TextField
                            label="Instructions (shown to the taker on the landing page)"
                            value={instructions} onChange={(e) => setInstructions(e.target.value)}
                            multiline minRows={8} fullWidth disabled={!testId}
                            helperText="Prefilled from this test's saved instructions, or a default template. Shared across all links for this test."
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                {results ? (
                    <>
                        <Button startIcon={<ContentCopyIcon />} onClick={copyAll}>Copy all</Button>
                        <Button onClick={() => setResults(null)}>Generate more</Button>
                        <Button variant="contained" color="secondary" onClick={onClose}>Done</Button>
                    </>
                ) : (
                    <>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button
                            variant="contained" color="secondary" onClick={handleGenerate} disabled={submitting}
                            startIcon={submitting ? <CircularProgress size={16} /> : null}
                        >
                            Generate {recipients.filter((r) => r.email.trim()).length || ""} link{recipients.filter((r) => r.email.trim()).length !== 1 ? "s" : ""}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default GenerateLinkDialog;
